import React, { useState } from "react";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from "firebase/firestore";

import { useApp } from "../../contexts/AppContext";
import { db } from "../../utils/firebase";
import { generatePartyCode } from "../../utils/partyCode";

import Button from "../Button/Button";

function CreatParty({ onCancel }: { onCancel: () => void }) {
  const {
    state: { user, event, globalUser },
    setSelectedPartyId,
  } = useApp();
  const [partyName, setPartyName] = useState("");
  const [displayName, setDisplayName] = useState(
    globalUser?.nickName ?? user?.displayName ?? ""
  );
  const [isCreating, setIsCreating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const canCreate =
    partyName.length >= 2 && displayName.trim().length > 0;

  async function createParty(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!canCreate || !event || !user || isCreating) return;

    setIsCreating(true);
    setErrorMessage("");

    try {
      let iterations = 0;
      const maxIterations = 5;
      let id: string | null = null;

      while (id === null && iterations < maxIterations) {
        const candidate = generatePartyCode();
        const q = query(
          collection(db, "parties"),
          where("shortId", "==", candidate)
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.docs.length === 0) {
          id = candidate;
        } else {
          iterations++;
        }
      }

      if (id === null) {
        setIsCreating(false);
        setErrorMessage("Unable to create party, please try again");
        return;
      }

      const docRef = await addDoc(collection(db, "parties"), {
        shortId: id,
        eventId: event._docId,
        name: partyName,
        created: serverTimestamp(),
        createdBy: user.uid,
      });

      if (docRef.id) {
        const partyUser = {
          active: true,
          userId: user.uid,
          name:
            displayName.trim() || globalUser?.nickName || user.displayName,
          partyId: docRef.id,
          eventId: event._docId,
          created: serverTimestamp(),
        };

        await addDoc(collection(db, "partyUsers"), partyUser);
        setSelectedPartyId(docRef.id);
      }
    } catch (err) {
      console.warn("Error creating party", err);
      setIsCreating(false);
    }
  }

  return (
    <form autoComplete="off" onSubmit={createParty}>
      <p className="text-center text-lg mb-4">Create a party and invite your friends and family to join!</p>
      <div className="mb-4">
        <label
          className="block text-lg text-esc-neutral font-medium mb-2"
          htmlFor="partyname"
        >
          Name of party
        </label>
        <input
          id="partyname"
          className="text-xl text-esc-secondary appearance-none border w-full py-2 px-3 leading-tight focus:outline-none focus:shadow-outline"
          type="text"
          name="name"
          value={partyName}
          onChange={(event) => setPartyName(event.target.value)}
          placeholder="Eg. 'Petras Party 🥳'"
          maxLength={20}
        />
      </div>
      <div className="mb-4">
        <label
          className="block text-lg text-esc-neutral font-medium mb-2"
          htmlFor="displayname"
        >
          Your name in this party
        </label>
        <input
          id="displayname"
          className="text-xl text-esc-secondary appearance-none border w-full py-2 px-3  leading-tight focus:outline-none focus:shadow-outline"
          type="text"
          name="displayname"
          value={displayName}
          onChange={(event) => setDisplayName(event.target.value)}
          placeholder="Your name"
          maxLength={23}
        />
      </div>
      {errorMessage && (
        <div className="mb-4 text-esc-accent-200 px-4 text-center leading-tight">
          {errorMessage}
        </div>
      )}

      <div className="flex justify-between">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!canCreate || isCreating}
          spinner={isCreating}
        >
          {isCreating ? "Creating" : "Create"}
        </Button>
      </div>
    </form>
  );
}

export default CreatParty;
